import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from '@/hooks';
import Button from '@/components/common/Button/Button';
import styles from './BonusGuestView.module.css';

const sliderData = [
  {
    title: 'Deposit Bonus',
    description:
      'Earn up to 360% bonus on deposits, with rewards unlocking as you wager. Deposit now to maximize your bonus!',
    ctaText: 'Deposit bonus details',
    buttonText: 'Get Started',
    type: 'bonus',
    img: '/assets/images/bonus/db-1.webp',
  },
  {
    title: 'Level Up Bonus',
    description:
      'Earn up to 360% bonus on deposits, with rewards unlocking as you wager. Deposit now to maximize your bonus!',
    ctaText: 'Level up bonus details',
    buttonText: 'Get Started',
    type: 'bonus',
    img: '/assets/images/bonus/db2.webp',
  },
  {
    title: 'Rakeback',
    description:
      'Earn up to 360% bonus on deposits, with rewards unlocking as you wager. Deposit now to maximize your bonus!',
    ctaText: 'Rakeback details',
    buttonText: 'Get Started',
    type: 'bonus',
    img: '/assets/images/bonus/db3.webp',
  },
  {
    title: 'Recharge Bonus',
    description:
      'Earn up to 360% bonus on deposits, with rewards unlocking as you wager. Deposit now to maximize your bonus!',
    ctaText: 'Recharge details',
    buttonText: 'Get Started',
    type: 'bonus',
    img: '/assets/images/bonus/db4.webp',
  },
  {
    title: 'Endless Benefits',
    description: 'Level Up Bonus',
    features: [
      {
        text: 'Level Up Bonus',
        image: '/assets/images/bonus/bgf1.webp',
      },
      {
        text: 'Weekly Bonus',
        image: '/assets/images/bonus/bf2.webp',
      },
      {
        text: 'Monthly Bonus',
        image: '/assets/images/bonus/bgf3.webp',
      },
      {
        text: 'Quest Hub',
        image: '/assets/images/bonus/bgf4.webp',
      },
      {
        text: 'Free Lucky Spins',
        image: '/assets/images/bonus/bgf5.webp',
      },
      {
        text: 'Free Roll Competition',
        image: '/assets/images/bonus/bgf6.webp',
      },
    ],
    buttonText: 'Get Started',
    type: 'benefits',
    image: '/assets/images/bonus/db5.webp',
  },
];

const BonusGuestView = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { isMobile, isDesktop } = useScreenSize();

  const handleSignupClick = () => {
    navigate('/login?modalType=auth-signup');
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
    containerRef.current.style.cursor = 'grabbing';
    containerRef.current.style.scrollBehavior = 'auto';
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
    containerRef.current.style.scrollBehavior = 'auto';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUpOrLeave = () => {
    setIsDragging(false);
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
      snapToNearestSlide();
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    snapToNearestSlide();
  };

  const snapToNearestSlide = () => {
    if (!containerRef.current) return;

    containerRef.current.style.scrollBehavior = 'smooth';
    const slideWidth = containerRef.current.clientWidth;
    const scrollPosition = containerRef.current.scrollLeft;
    const slideIndex = Math.round(scrollPosition / slideWidth);

    setCurrentSlide(slideIndex);
    containerRef.current.scrollTo({
      left: slideIndex * slideWidth,
      behavior: 'smooth',
    });
  };

  const goToSlide = (index) => {
    if (!containerRef.current) return;
    setCurrentSlide(index);
    const slideWidth = containerRef.current.clientWidth;
    containerRef.current.scrollTo({
      left: slideWidth * index,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const slideWidth = containerRef.current.clientWidth;
        containerRef.current.scrollLeft = currentSlide * slideWidth;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentSlide]);

  // Web view: Grid layout
  if (isDesktop) {
    return (
      <div className={styles.webContainer}>
        <div className={styles.webHeader}>
          <h1 className={styles.webTitle}>Bonus & Rewards</h1>
          <p className={styles.webSubtitle}>
            Unlock amazing bonuses and rewards as you play. Choose your bonus and get started!
          </p>
        </div>

        <div className={styles.bonusGrid}>
          {sliderData.map((slide, index) => (
            <div key={index} className={styles.bonusCard}>
              <div className={styles.cardImageWrapper}>
                <img
                  src={slide.img || slide.image}
                  alt={slide.title}
                  className={styles.cardImage}
                />
              </div>
              <div className={styles.cardContent}>
                <h2 className={styles.cardTitle}>{slide.title}</h2>
                {slide.type === 'bonus' ? (
                  <>
                    <p className={styles.cardDescription}>{slide.description}</p>
                    <a href="#details" className={styles.cardLink}>
                      {slide.ctaText}
                    </a>
                  </>
                ) : (
                  <div className={styles.cardFeaturesGrid}>
                    {slide.features?.slice(0, 4).map((feature, idx) => (
                      <div key={idx} className={styles.cardFeatureItem}>
                        <img
                          src={feature?.image}
                          alt={feature?.text}
                          className={styles.cardFeatureIcon}
                        />
                        <span>{feature?.text}</span>
                      </div>
                    ))}
                  </div>
                )}
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleSignupClick}
                  className={styles.cardButton}
                  fullWidth
                >
                  {slide.buttonText}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Mobile view: Slider (keep existing)
  return (
    <div className={styles.sliderContainer}>
      <div
        ref={containerRef}
        className={styles.slidesWrapper}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUpOrLeave}
        onMouseLeave={handleMouseUpOrLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {sliderData.map((slide, index) => (
          <div key={index} className={styles.slide}>
            <div className={styles.slideContent}>
              <img alt={index} src={slide.img || slide.image} className={styles.imagePlaceholder} />
              <div className={styles.slideTextContent}>
                <h1 className={styles.slideTitle}>{slide.title}</h1>

                <div>
                  {slide.type === 'bonus' ? (
                    <>
                      <p className={styles.slideDescription}>{slide.description}</p>
                      <a href="#details" className={styles.ctaLink}>
                        {slide.ctaText}
                      </a>
                    </>
                  ) : (
                    <div className={styles.featuresGrid}>
                      {slide.features.map((feature, idx) => (
                        <div key={idx} className={styles.featureItem}>
                          <img
                            src={feature?.image}
                            alt={idx}
                            className={styles.featureIcon}
                          />
                          <span>{feature?.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className={styles.dots}>
                  {sliderData.map((_, idx) => (
                    <button
                      key={idx}
                      className={`${styles.dot} ${currentSlide === idx ? styles.active : ''}`}
                      onClick={() => goToSlide(idx)}
                    />
                  ))}
                </div>

                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleSignupClick}
                  className={styles.ctaButton}
                >
                  {slide.buttonText}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BonusGuestView;
